/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import styles from 'src/shared/app/authentication/component/support/base/BaseSupport.style';

import LazyLoginMain from './LazyLoginSupportMain';
import LazyLoginLinkMain from './link/LazyLoginLinkMain';
import LazyRetrieveUsernameMain from './username/LazyRetrieveUsernameMain';
import SupportLink from 'src/shared/app/authentication/component/support/base/SupportLink';
import LazySupportContactMain from 'src/shared/app/authentication/component/support/contact/LazySupportContactMain';
import messages from 'src/shared/app/authentication/l10n/authenticationSupportL10n';
import ZIndexTopBar from 'src/shared/app/base/component/template/component/TopBar';
import navMessages from 'src/shared/app/nav/l10n/navigationL10n';
import TopBar from 'src/shared/app/nav/component/top-bar/TopBar';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import {
  loginRoute,
  retrieveUsernameRoute,
  loginLinkRoute,
  contactRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import CircledArrowIcon from 'src/shared/ui/component/icon/CircledArrowIcon';
import MailWarningIcon from 'src/shared/ui/component/icon/MailWarningIcon';
import QuestionIcon from 'src/shared/ui/component/icon/QuestionIcon';
import InstructionText from 'src/shared/ui/component/text/InstructionText';

type Props = {
  isUsernameRetrievalAllowed: boolean,
  isLoginLinkDisabled: boolean,
};

const useStyles = createUseStyles(styles);

function LoginSupportSection({
  isUsernameRetrievalAllowed,
  isLoginLinkDisabled,
}: Props): React.Node {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const [
    loginPath,
    retrieveUsernamePath,
    loginLinkPath,
    contactPath,
  ] = useRoutes(
    loginRoute,
    retrieveUsernameRoute,
    loginLinkRoute,
    contactRoute,
  );

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    LazyLoginMain.preload();
    LazyRetrieveUsernameMain.preload();
    LazyLoginLinkMain.preload();
    LazySupportContactMain.preload();
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const retrieveEmailElt = isUsernameRetrievalAllowed && (
    <li className={classes.item}>
      <SupportLink
        title={intl.formatMessage(messages.retrieveEmailTitle)}
        cta={intl.formatMessage(messages.retrieveEmailCta)}
        href={retrieveUsernamePath}
        icon={<MailWarningIcon />}
      />
    </li>
  );

  const loginLinkElt = !isLoginLinkDisabled && (
    <li className={classes.item}>
      <SupportLink
        title={intl.formatMessage(messages.loginLinkTitle)}
        cta={intl.formatMessage(messages.loginLinkCta)}
        href={loginLinkPath}
        icon={<CircledArrowIcon />}
      />
    </li>
  );

  return (
    <section className={classes.section}>
      <ZIndexTopBar>
        <TopBar
          title={intl.formatMessage(messages.loginHeading)}
          cancelLabel={intl.formatMessage(navMessages.cancel)}
          backTo={loginPath}
        />
      </ZIndexTopBar>
      <div className={classes.instruction}>
        <InstructionText>
          {intl.formatMessage(messages.description)}
        </InstructionText>
      </div>
      <ul className={classes.list}>
        {retrieveEmailElt}
        {loginLinkElt}
        <li className={classes.item}>
          <SupportLink
            title={intl.formatMessage(messages.contactSupportTitle)}
            cta={intl.formatMessage(messages.contactSupportCta)}
            href={contactPath}
            icon={<QuestionIcon />}
          />
        </li>
      </ul>
    </section>
  );
}

export default compose(React.memo)(LoginSupportSection);
